import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import {
  Box, Grid, Typography, Paper, Stack, Chip, useTheme, Button, Alert,
  Divider, IconButton, Tooltip, LinearProgress, Avatar, alpha
} from "@mui/material";
import {
  TrendingUpOutlined as TrendingUpIcon,
  PeopleOutlined as PeopleIcon,
  LocalActivityOutlined as BookingIcon,
  CurrencyRupeeOutlined as RevenueIcon,
  CalendarMonthOutlined as ScheduleIcon,
  ChairAltOutlined as ScreenIcon,
  MovieOutlined as MovieIcon,
  SensorsOutlined as LiveIcon,
  HistoryToggleOffOutlined as HistoryIcon,
  ArrowForwardIos as ChevronIcon,
  MoreVert as MoreIcon,
  Sync as SyncIcon
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { getDashboardSummary } from "../../store/slices/partnerSlice";
import { useSocket } from "../../contexts/SocketContext";
import ModernLoader from "../../components/common/ModernLoader";

const StatCard = ({ title, value, subtitle, icon, color, trend, delay = 0, colors, mode }) => {
  const cardStyle = {
    bgcolor: colors.background,
    borderRadius: "12px",
    boxShadow: mode === 'dark' ? "0 4px 18px 0 rgba(0,0,0, 0.4)" : "0 2px 10px 0 rgba(76, 78, 100, 0.08)",
    height: "100%",
    p: 2.5,
    display: "flex",
    flexDirection: "column",
    position: "relative",
    border: `1px solid ${colors.divider}`,
    transition: 'all .25s ease-in-out',
    "&:hover": { transform: 'translateY(-2px)', boxShadow: mode === 'dark' ? "0 6px 24px 0 rgba(0,0,0, 0.6)" : "0 4px 16px 0 rgba(76, 78, 100, 0.15)" }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay }} style={{ height: '100%' }}>
      <Card sx={cardStyle}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Avatar sx={{ bgcolor: alpha(color, 0.12), color: color, borderRadius: "10px", width: 44, height: 44 }}>
            {React.cloneElement(icon, { sx: { fontSize: 24 } })}
          </Avatar>
          {trend && (
            <Typography variant="caption" sx={{ color: colors.success, fontWeight: 700, bgcolor: alpha(colors.success, 0.1), px: 1, py: 0.2, borderRadius: '4px', height: 'fit-content' }}>
              {trend}
            </Typography>
          )}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.textPrimary, mb: 0.5, letterSpacing: '-0.02em' }}>{value}</Typography>
        <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 600, fontSize: '0.85rem' }}>{title}</Typography>
        {subtitle && <Typography variant="caption" sx={{ color: colors.textSecondary, mt: 0.5, opacity: 0.7, fontWeight: 500 }}>{subtitle}</Typography>}

        <Box sx={{ mt: 'auto', pt: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end', height: 16 }}>
            {[40, 70, 45, 90, 65].map((h, j) => (
              <Box key={j} sx={{ flex: 1, height: `${h}%`, bgcolor: j === 3 ? color : alpha(color, 0.15), borderRadius: '2px' }} />
            ))}
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
};

// Internal Card Component for reuse
const Card = ({ children, sx }) => <Paper elevation={0} sx={sx}>{children}</Paper>;

const PartnerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const { user: clerkUser } = useUser();
  const { partner, dashboardStats, loading, profileChecked, isPartner } = useSelector(state => state.partner);
  const { socket: socketInstance } = useSocket();

  // Materio Exact Palette for High-Fidelity Sync
  const colors = useMemo(() => ({
    primary: "#666CFF",
    success: "#72E128",
    warning: "#FDB528",
    error: "#FF4D49",
    info: "#26C6F9",
    textPrimary: mode === 'dark' ? "rgba(231, 227, 252, 0.87)" : "rgba(58, 53, 65, 0.87)",
    textSecondary: mode === 'dark' ? "rgba(231, 227, 252, 0.6)" : "rgba(58, 53, 65, 0.6)",
    background: mode === 'dark' ? "#282A42" : "#ffffff",
    divider: mode === 'dark' ? "rgba(231, 227, 252, 0.12)" : "rgba(58, 53, 65, 0.12)",
  }), [mode]);

  const cardStyle = {
    bgcolor: colors.background,
    borderRadius: "12px",
    boxShadow: mode === 'dark' ? "0 4px 18px 0 rgba(0,0,0, 0.4)" : "0 2px 10px 0 rgba(76, 78, 100, 0.12)",
    border: `1px solid ${colors.divider}`,
    transition: 'all .25s ease-in-out',
  };

  const fetchStats = useCallback(() => {
    if (clerkUser?.id && isPartner) {
      dispatch(getDashboardSummary(clerkUser.id));
    }
  }, [dispatch, clerkUser?.id, isPartner]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (socketInstance) {
      const events = ["seats-booked", "show-updated", "theater-updated", "partner-updated"];
      events.forEach(e => socketInstance.on(e, fetchStats));
      return () => events.forEach(e => socketInstance.off(e, fetchStats));
    }
  }, [socketInstance, fetchStats]);

  if (!profileChecked || (loading && !dashboardStats)) return <ModernLoader text="Deciphering Real-time Analytics..." />;

  const stats = dashboardStats || { totalShows: 0, activeShows: 0, totalBookings: 0, totalRevenue: 0, todayBookings: 0, totalSeatsBooked: 0 };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const quickActions = [
    { label: "Audit Screens", icon: <ScreenIcon />, path: "/partner/screens", color: colors.primary, desc: "Manage geometries" },
    { label: "Scheduling", icon: <ScheduleIcon />, path: "/partner/shows", color: "#8b5cf6", desc: "Orchestrate slots" },
    { label: "Bookings", icon: <BookingIcon />, path: "/partner/bookings", color: colors.info, desc: "Verify ledger" },
    { label: "QR Intel", icon: <LiveIcon />, path: "/partner/scanner", color: colors.warning, desc: "Validate access" },
  ];

  return (
    <Box sx={{ pb: 6 }}>
      {/* Header Section */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.5 }}>
            Operations Core
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
            Theater ecosystem synchronized with real-time telemetry
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Chip icon={<LiveIcon sx={{ fontSize: '14px !important' }} />} label="SYNCED" size="small" sx={{ height: 28, bgcolor: alpha(colors.success, 0.1), color: colors.success, fontWeight: 800, fontSize: '0.65rem' }} />
          <Button
            variant="contained"
            onClick={fetchStats}
            startIcon={<SyncIcon />}
            sx={{ borderRadius: "10px", px: 2.5, bgcolor: colors.primary, fontSize: '0.8rem', fontWeight: 700 }}
          >
            Force Sync
          </Button>
        </Stack>
      </Box>

      {/* Subscription Alert */}
      {partner && partner.isSubscriptionExpired && (
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}>
          <Alert severity="error" sx={{ mb: 4, borderRadius: '12px', fontWeight: 700, boxShadow: '0 4px 12px rgba(255,77,73,0.15)' }}>
            {partner.pushNotificationMessage || '⚠️ SUBSCRIPTION EXPIRED: Theater nodes are currently in standby. Please renew.'}
          </Alert>
        </motion.div>
      )}

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Confirmed Revenue" value={formatCurrency(stats.totalRevenue)} subtitle={`${stats.totalBookings} total transactions`} icon={<RevenueIcon />} color={colors.success} trend="+12.5%" delay={0.1} colors={colors} mode={mode} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Membership Tier"
            value={partner?.currentPlan || 'Starter'}
            subtitle={partner?.subscriptionEndsAt ? `Ends on ${new Date(partner.subscriptionEndsAt).toLocaleDateString()}` : 'Lifetime Starter'}
            icon={<HistoryIcon />}
            color={colors.primary}
            delay={0.2}
            colors={colors} mode={mode}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Auditorium Load" value={stats.totalSeatsBooked} subtitle="Seats occupied in cycle" icon={<ScreenIcon />} color={colors.info} trend="+8.2%" delay={0.3} colors={colors} mode={mode} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Daily Velocity" value={stats.todayBookings} subtitle="Active transactions (24h)" icon={<BookingIcon />} color={colors.warning} delay={0.4} colors={colors} mode={mode} />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Orchestration */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ ...cardStyle, p: 3, h: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.textPrimary }}>Quick Orchestration</Typography>
              <IconButton size="small"><MoreIcon /></IconButton>
            </Box>
            <Stack spacing={1.5}>
              {quickActions.map((action, idx) => (
                <Button
                  key={idx}
                  onClick={() => navigate(action.path)}
                  sx={{
                    p: 1.5, borderRadius: '12px', justifyContent: 'flex-start', textAlign: 'left',
                    bgcolor: alpha(colors.textPrimary, 0.03),
                    transition: '0.2s',
                    '&:hover': { bgcolor: alpha(action.color, 0.1), transform: 'translateX(4px)' }
                  }}
                >
                  <Avatar sx={{ bgcolor: alpha(action.color, 0.15), color: action.color, borderRadius: '10px', mr: 2, width: 40, height: 40 }}>
                    {action.icon}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: colors.textPrimary }}>{action.label}</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: colors.textSecondary, fontWeight: 500 }}>{action.desc}</Typography>
                  </Box>
                  <ChevronIcon sx={{ fontSize: 14, opacity: 0.3, color: colors.textPrimary }} />
                </Button>
              ))}
            </Stack>
          </Card>
        </Grid>

        {/* Real-time Ledger / Analytics Preview */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ ...cardStyle, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.textPrimary }}>Channel Performance</Typography>
                <Typography variant="body2" sx={{ color: colors.textSecondary, fontWeight: 500 }}>Synchronized with global operational ledger</Typography>
              </Box>
              <Chip label="Real-time Analytics" size="small" variant="outlined" sx={{ fontWeight: 700, borderRadius: "8px", color: colors.primary, borderColor: colors.primary }} />
            </Box>

            <Alert icon={<LiveIcon sx={{ fontSize: 20 }} />} sx={{ mb: 4, borderRadius: '12px', bgcolor: alpha(colors.info, 0.05), color: colors.info, border: `1px solid ${alpha(colors.info, 0.1)}`, '& .MuiAlert-icon': { color: colors.info } }}>
              Theater ecosystem connected to the high-speed synchronization mesh. Automatic audits active.
            </Alert>

            <Grid container spacing={4} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary }}>Occupancy Rate</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary, fontWeight: 500 }}>Global network average</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: colors.primary }}>
                      {Math.round((stats.totalSeatsBooked / (stats.totalShows * 100 || 1)) * 100)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (stats.totalSeatsBooked / (stats.totalShows * 100 || 1)) * 100)}
                    sx={{ height: 10, borderRadius: 5, bgcolor: alpha(colors.textPrimary, 0.05), '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: colors.primary } }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary }}>Active Show Load</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: colors.textSecondary, fontWeight: 500 }}>Node utilization frequency</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#8b5cf6' }}>
                      {Math.round((stats.activeShows / (stats.totalShows || 1)) * 100)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, (stats.activeShows / (stats.totalShows || 1)) * 100)}
                    sx={{ height: 10, borderRadius: 5, bgcolor: alpha(colors.textPrimary, 0.05), '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: '#8b5cf6' } }}
                  />
                </Stack>
              </Grid>
            </Grid>

            {/* Micro Visual Chart Mockup (Matching Admin Style) */}
            <Box sx={{ mt: 4, display: 'flex', gap: 1, alignItems: 'flex-end', height: 80, px: 1 }}>
              {[30, 45, 25, 60, 80, 50, 90, 40, 70, 35, 85, 45].map((h, i) => (
                <Box key={i} sx={{ flex: 1, height: `${h}%`, bgcolor: i === 6 ? colors.primary : alpha(colors.primary, 0.1), borderRadius: '4px' }} />
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartnerDashboard;
